import React, { createRef, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  arrayOf as arrayType,
  bool as boolType,
  customType,
  extend,
  params,
  QueryContext,
  shape as shapeType,
  string as stringType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { StickyWithHeaderObserver } from '@thd-olt-component-react/sticky';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { ThemeContext } from '@thd-olt-component-react/theme-provider';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { CustomerContext } from '@thd-olt-functional/customer-information';
import { CategoryNav } from './subcomponents/category-nav/CategoryNav';
import { ProductsRetrieval } from './ProductsRetrieval';
import { CategoryHeader } from './CategoryHeader';
import { decodePromoType, formatImage, getBackupIds, isValidData } from '../utils';

export const SpecialBuyPromos = ({ specialBuyType, previewDate, backupCategories, dataFailureCallback }) => {
  declareContexts([ExperienceContext, QueryContext, ThemeContext, CustomerContext]);
  const categoryNavRef = useRef(null);
  const reportedCategories = useRef([]);

  const reportCategoryAnalytics = (category) => {
    if (!reportedCategories.current.includes(category.id)) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('special-buy-promos.category', category.itemIds);
      reportedCategories.current.push(category.id);
    }
  };

  const { data, loading } = useDataModel('specialBuyMetadata', {
    ssr: !previewDate,
    variables: {
      previewDate,
      dealType: decodePromoType({ promoType: specialBuyType }),
      backupCategories
    },
    context: { withSSO: !!previewDate }
  });

  const backUpCategories = useMemo(() => {
    if (!isValidData(data)) {
      if (data && typeof dataFailureCallback === 'function') {
        dataFailureCallback({ data });
      }
      return null;
    }
    return data.specialBuyMetadata.categoryMetadata
      .slice(3)
      .filter((category) => {
        return Boolean(
          category.itemIds?.length
          && category.tagline
          && category.name
          && category.name.toLowerCase().includes('backup')
        );
      })
      .map((category) => {
        return {
          id: category.name.replace(/[^A-Za-z]/g, ''),
          itemIds: Array.from(new Set(category.itemIds)),
        };
      });
  }, [data, dataFailureCallback]);

  const specialBuyCategories = useMemo(() => {
    if (!isValidData(data)) {
      if (data && typeof dataFailureCallback === 'function') {
        dataFailureCallback({ data });
      }
      return null;
    }
    return data.specialBuyMetadata.categoryMetadata
      .slice(0, 3)
      .filter((category) => {
        return Boolean(
          category.itemIds?.length
          && category.tagline
          && category.name
          && !category.name.toLowerCase().includes('backup')
        );
      })
      .map((category) => {
        return {
          id: category.name.replace(/[^A-Za-z]/g, ''),
          imageURL: formatImage({ src: category.image?.url, imageSize: '65' }),
          itemIds: Array.from(new Set(category.itemIds)),
          sectionRef: createRef(),
          subtitle: category.tagline,
          title: category.name,
        };
      });
  }, [data, dataFailureCallback]);

  useEffect(() => {
    if (specialBuyCategories?.[0]?.itemIds && !loading) {
      reportCategoryAnalytics(specialBuyCategories[0]);
    }
  }, [specialBuyCategories, loading]);

  const hasBackups = backUpCategories?.length > 0;

  if (!specialBuyCategories?.length) return null;
  return (
    <div data-component="SpecialBuyPromos" data-testid="special-buy-promos">
      <CategoryNav
        categories={specialBuyCategories}
        onClick={reportCategoryAnalytics}
        forwardRef={categoryNavRef}
      />
      <StickyWithHeaderObserver
        forwardRef={categoryNavRef}
        position="top"
        offsetScroll={100}
        stickyType="forwardRef"
        stickyByDefault
        className="sui-w-full sui-left-0 sui-z-max"
      >
        <CategoryNav
          categories={specialBuyCategories}
          onClick={reportCategoryAnalytics}
          showSelected
        />
      </StickyWithHeaderObserver>
      {specialBuyCategories.map((category, index) => {
        const immediateRender = index === 0;
        const {
          sectionRef, title, subtitle, id, itemIds
        } = category;

        const backupItemIds = hasBackups ? getBackupIds(backUpCategories, id) : [];

        return (
          <div className="sui-row-span-1 sui-mt-8 sui-mb-2 sui-p-1" ref={sectionRef} key={title}>
            <CategoryHeader title={title} subtitle={subtitle} id={id} />
            <Hydrator
              id={`products-grid-${index}`}
              key={`products-grid-${index}`}
              className="sui-grid"
              immediateRender={immediateRender}
              scrollBuffer={1400}
            >
              <ProductsRetrieval
                id={id}
                itemIds={itemIds}
                shouldSsrFirstProducts={immediateRender}
                backupItemIds={backupItemIds}
                specialBuyType={specialBuyType}
              />
            </Hydrator>
          </div>
        );
      })}
    </div>
  );
};

SpecialBuyPromos.displayName = 'SpecialBuyPromos';

SpecialBuyPromos.propTypes = {
  /** sbotd admins may use a previewDate to see special buys for the past or future. requires THDSSO myapron login */
  previewDate: PropTypes.string,
  /** It identifies which special buy type to request the data for */
  specialBuyType: PropTypes.oneOf(['sbotd', 'pro']).isRequired,
  /** When the data is invalid the failure callback function will get call */
  dataFailureCallback: PropTypes.func,
  /** If true, backup categories will be fetched and used for out-of-stock item replacement */
  backupCategories: PropTypes.bool,
};

SpecialBuyPromos.defaultProps = {
  previewDate: null,
  dataFailureCallback: null,
  backupCategories: null
};

SpecialBuyPromos.dataModel = extend(
  {
    specialBuyMetadata: params({
      dealType: customType('DealType').enum(['DAY', 'PRO', 'ALL'], 'DAY'),
      previewDate: stringType(),
      backupCategories: boolType()
    })
      .shape({
        categoryMetadata: arrayType({
          itemIds: arrayType(stringType()),
          tagline: stringType(),
          name: stringType(),
          image: shapeType({
            url: stringType()
          })
        })
      })
  },
  ProductsRetrieval,
);
